import React, { PureComponent } from 'react';
import R from 'ramda';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  TRANSITION_STATUS_PRE_TRANSITION,
  TRANSITION_STATUS_TRANSITIONING,
  isTransitioning,
} from '../../modules/transitionStatus';
import CarouselGrid from '../CarouselGrid';

class CarouselBelt extends PureComponent {
  componentDidUpdate() {
    const { setTransitionStart, carouselsStoreDataKey, transitionStatus } = this.props;

    if (transitionStatus === TRANSITION_STATUS_PRE_TRANSITION) {
      setTransitionStart({ carouselsStoreDataKey });
    }
  }

  onTransitionEnd = (e) => {
    const { setTransitionEnd, carouselsStoreDataKey, transitionStatus } = this.props;

    const isTransitionComplete = (
      e.propertyName === 'transform' &&
      transitionStatus === TRANSITION_STATUS_TRANSITIONING
    );

    if (isTransitionComplete) {
      setTransitionEnd({ carouselsStoreDataKey });
    }
  }

  getIndex() {
    const { currentIndex, previousIndex, transitionStatus } = this.props;
    return transitionStatus === TRANSITION_STATUS_PRE_TRANSITION ? previousIndex : currentIndex;
  }

  getWrapperStyles() {
    const { transitionStatus } = this.props;
    const styles = {};

    if (isTransitioning(transitionStatus)) {
      styles.width = this.wrapper.offsetWidth;
      styles.height = this.wrapper.offsetHeight;
      styles.display = 'block';
    }

    return styles;
  }

  getBeltStyles() {
    const { transitionStatus, noOfPages } = this.props;
    const styles = {};

    if (transitionStatus === TRANSITION_STATUS_TRANSITIONING) {
      styles.transition = '';
    } else {
      styles.transition = 'none';
    }

    if (isTransitioning(transitionStatus)) {
      const index = this.getIndex();
      const itemWidth = 100 / noOfPages;
      const beltWidth = 100 * noOfPages;
      const beltPosition = index ? itemWidth * index : 0;

      styles.width = `${beltWidth}%`;
      styles.transform = `translateX(-${beltPosition}%)`;
    }

    return styles;
  }

  getInfiniteBeltStyles() {
    const { transitionStatus, currentIndex, previousIndex } = this.props;

    if (transitionStatus === TRANSITION_STATUS_PRE_TRANSITION) {
      return {
        transition: 'none',
        width: '300%',
        transform: 'translateX(-33.33%)',
      };
    } else if (transitionStatus === TRANSITION_STATUS_TRANSITIONING) {
      const isForward = currentIndex > previousIndex;
      const beltPosition = isForward ? -66.66 : 0;
      return {
        transition: '',
        width: '300%',
        transform: `translateX(${beltPosition}%)`,
      };
    }

    return {
      transition: 'none',
      width: '',
    };
  }

  getPage({ startIndex }) {
    const { children, noPerPage } = this.props;

    const items = R.compose(
      R.map((index) => {
        if (index < 0) {
          return R.prop(children.length + index, children);
        }

        if (index >= children.length) {
          return R.prop(index - children.length, children);
        }

        return R.prop(index, children);
      }),
      R.range(startIndex),
    )(startIndex + noPerPage);

    return <CarouselGrid>{items}</CarouselGrid>;
  }

  getPages() {
    const {
      children,
      noPerPage,
      noOfPages,
      previousItemIndex,
      currentItemIndex,
      transitionStatus,
      infinite,
    } = this.props;

    if (infinite) {
      const index = isTransitioning(transitionStatus) ? previousItemIndex : currentItemIndex;
      const prevPage = this.getPage({ startIndex: index - noPerPage });
      const currentPage = this.getPage({ startIndex: index });
      const nextPage = this.getPage({ startIndex: index + noPerPage });

      return [
        prevPage,
        currentPage,
        nextPage,
      ];
    } else if (noPerPage > 1) {
      return R.compose(
        R.map(index => (
          <CarouselGrid>
            { children.slice(index * noPerPage, (index * noPerPage) + noPerPage) }
          </CarouselGrid>
        )),
        R.range(0),
      )(noOfPages);
    }

    return children;
  }

  generateCarouselPageClassNames(itemIndex) {
    const { transitionStatus, infinite } = this.props;
    const index = this.getIndex();
    const isHidden = infinite ? (
      itemIndex !== 1 && !isTransitioning(transitionStatus)
    ) : (
      index !== itemIndex && !isTransitioning(transitionStatus)
    );

    return classNames('carousel__page', {
      'carousel__page--hidden': isHidden,
    });
  }

  render() {
    const { pageIds, infinite } = this.props;
    const pages = this.getPages();
    const beltStyles = infinite ? this.getInfiniteBeltStyles() : this.getBeltStyles();

    return (
      <div
        className="carousel__wrapper"
        ref={(el) => { this.wrapper = el; }}
        style={this.getWrapperStyles()}
      >
        <ul
          onTransitionEnd={this.onTransitionEnd}
          style={beltStyles}
          className="carousel__belt flex-container"
        >
          {
            pages.map((page, index) => (
              <li
                key={pageIds[index]}
                className={this.generateCarouselPageClassNames(index)}
              >
                { page }
              </li>
            ))
          }
        </ul>
      </div>
    );
  }
}

CarouselBelt.propTypes = {
  children: PropTypes.node.isRequired,
  currentIndex: PropTypes.number.isRequired,
  previousIndex: PropTypes.number.isRequired,
  transitionStatus: PropTypes.string.isRequired,
  setTransitionStart: PropTypes.func.isRequired,
  setTransitionEnd: PropTypes.func.isRequired,
  noOfPages: PropTypes.number.isRequired,
  carouselsStoreDataKey: PropTypes.string.isRequired,
  pageIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  noPerPage: PropTypes.number,
  previousItemIndex: PropTypes.number,
  currentItemIndex: PropTypes.number,
  infinite: PropTypes.bool,
};

export default CarouselBelt;
